import { NavLink, Outlet, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Modal } from "./ui";
import {
  LayoutDashboard, Scale, FileCheck, MessagesSquare, Trash2, Droplets,
  Building2, Leaf, ShieldAlert, ClipboardCheck, Handshake, Lightbulb,
  Truck, Map, FlaskConical, BookOpen, Settings, LogOut, Users, Eye, X, Bell,
  FileSpreadsheet, CalendarClock, Archive, Pencil, Save
} from "lucide-react";

const MODULES = [
  { group: "PANEL", path: "/dashboard", label: "Panel Principal", icon: LayoutDashboard },
  { group: "CUMPLIMIENTO", path: "/legal", label: "Requisitos Legales", icon: Scale },
  { group: "CUMPLIMIENTO", path: "/iso14001", label: "ISO 14001 / SGA", icon: FileCheck },
  { group: "CUMPLIMIENTO", path: "/audits", label: "Auditorías y RACS", icon: ClipboardCheck },
  { group: "CUMPLIMIENTO", path: "/studies", label: "Estudios Normativos", icon: FlaskConical },
  { group: "OPERACIÓN", path: "/waste", label: "Gestión de Residuos", icon: Trash2 },
  { group: "OPERACIÓN", path: "/waste-intake", label: "Citas de Almacén", icon: CalendarClock },
  { group: "OPERACIÓN", path: "/resources", label: "Consumo de Recursos", icon: Droplets },
  { group: "OPERACIÓN", path: "/pemex", label: "Soporte PEMEX", icon: Building2 },
  { group: "OPERACIÓN", path: "/brigades", label: "Respuesta a Emergencias", icon: ShieldAlert },
  { group: "SAFETY & HEALTH", path: "/investigations", label: "Investigaciones HSEQ", icon: ClipboardCheck },
  { group: "ESTRATÉGICO", path: "/esg", label: "ESG - RSE", icon: Leaf },
  { group: "ESTRATÉGICO", path: "/advc", label: "Compromisos Voluntarios", icon: Handshake },
  { group: "ESTRATÉGICO", path: "/smart", label: "Smart Projects", icon: Lightbulb },
  { group: "RECURSOS", path: "/providers", label: "Proveedores Ambientales", icon: Truck },
  { group: "RECURSOS", path: "/map", label: "Mapa Interactivo", icon: Map },
  { group: "RECURSOS", path: "/formats", label: "Formatos", icon: FileSpreadsheet },
  { group: "RECURSOS", path: "/repository", label: "Repositorio", icon: Archive },
];

export default function Layout() {
  const { user, logout, exitAudit, fetchMe } = useAuth();
  const navigate = useNavigate();
  const isAuditing = user?.role === "SUPER_ADMIN" && user?.audit_tenant_id;
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const [unread, setUnread] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ installation_name: "", installation_location: "", area: "", department: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  // SECCIÓN J — Multi-instalación: lista y activa (oculto si solo hay 1)
  const [installations, setInstallations] = useState([]);
  const [activeInstId, setActiveInstId] = useState(null);

  useEffect(() => {
    if (!user || user.role === "SUPER_ADMIN") return;
    let cancelled = false;
    (async () => {
      try {
        const [iRes, sRes] = await Promise.all([
          api.get("/installations"),
          api.get("/session/active").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setInstallations(iRes.data || []);
        setActiveInstId(sRes.data?.active_installation_id || (iRes.data?.[0]?.id ?? null));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user]);

  async function changeActiveInstallation(instId) {
    setActiveInstId(instId);
    try {
      await api.post("/session/set-active", { installation_id: instId });
      // Soft refresh: dispatch a global event modules can listen to
      window.dispatchEvent(new CustomEvent("installation-changed", { detail: { installation_id: instId } }));
    } catch (e) {
      console.error("set-active failed", e);
    }
  }

  const installation = user?.installation_name
    || user?.tenant?.installation_name
    || user?.tenant?.name
    || user?.audit_tenant?.name
    || "Sin instalación";

  function openProfile() {
    setProfileForm({
      installation_name: user?.installation_name || "",
      installation_location: user?.installation_location || "",
      area: user?.area || "",
      department: user?.department || "",
    });
    setProfileError("");
    setShowProfile(true);
  }

  async function saveProfile() {
    setProfileSaving(true);
    setProfileError("");
    try {
      await api.patch("/auth/me", profileForm);
      await fetchMe();
      setShowProfile(false);
    } catch (e) {
      setProfileError(e?.response?.data?.detail || e.message || "No se pudo guardar el perfil");
    } finally {
      setProfileSaving(false);
    }
  }

  useEffect(() => {
    if (user?.role === "SUPER_ADMIN" && !user?.audit_tenant_id) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications/inbox/unread-count");
        setUnread(data.unread);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [user]);


  async function handleExitAudit() {
    await exitAudit();
    navigate("/superadmin");
  }

  return (
    <div>
      {isAuditing && (
        <div className="audit-banner" data-testid="audit-banner">
          <span>👁 Viendo como auditor — Tenant: <b>{user.audit_tenant?.name || "Tenant"}</b></span>
          <button className="btn btn-sm" onClick={handleExitAudit} data-testid="exit-audit-btn">
            <X size={14} /> Salir del modo auditoría
          </button>
        </div>
      )}
      <div className="app-shell">
        <aside className="sidebar" data-testid="sidebar">
          <div className="sidebar-brand">
            <div className="sidebar-brand-logo">SGA</div>
            <div>
              <div className="sidebar-brand-text">SGA Xerome</div>
              <div className="sidebar-brand-sub" data-testid="sidebar-installation" title="Instalación (click para editar)" style={{ cursor: "pointer" }} onClick={openProfile}>
                {installation} <Pencil size={10} style={{ display: "inline", opacity: 0.6, marginLeft: 4 }} />
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            {MODULES.map((m, idx) => {
              const Icon = m.icon;
              const showLabel = idx === 0 || MODULES[idx - 1].group !== m.group;
              return (
                <React.Fragment key={m.path}>
                  {showLabel && <div className="sidebar-section-label">{m.group}</div>}
                  <NavLink
                    to={m.path}
                    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                    data-testid={`nav-${m.path.replace("/", "")}`}
                  >
                    <span className="nav-item-icon"><Icon size={16} /></span>
                    <span>{m.label}</span>
                  </NavLink>
                </React.Fragment>
              );
            })}
            {isAdmin && (
              <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-admin">
                <span className="nav-item-icon"><Users size={16} /></span>
                <span>Administración</span>
              </NavLink>
            )}
            {isAdmin && (
              <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-settings">
                <span className="nav-item-icon"><Settings size={16} /></span>
                <span>Settings</span>
              </NavLink>
            )}
            {user?.role === "SUPER_ADMIN" && !isAuditing && (
              <NavLink to="/superadmin" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-superadmin">
                <span className="nav-item-icon"><Eye size={16} /></span>
                <span>Super Admin</span>
              </NavLink>
            )}
          </nav>
        </aside>

        <div className="main">
          <div className="topbar">
            <div>
              <div className="topbar-title" data-testid="topbar-installation" style={{ cursor: "pointer" }} onClick={openProfile} title="Click para editar tu instalación">
                {installation}
              </div>
              <div className="topbar-sub">
                Sistema de Gestión Ambiental
                {user?.installation_location && <span> · {user.installation_location}</span>}
                {installations.length >= 2 && (
                  <span className="ml-2">
                    {" · "}
                    <select
                      data-testid="installation-selector"
                      value={activeInstId || ""}
                      onChange={(e) => changeActiveInstallation(e.target.value)}
                      style={{
                        background: "var(--bg-2)", color: "var(--text)", border: "1px solid var(--border-2)",
                        borderRadius: 4, padding: "2px 6px", fontSize: 12,
                      }}
                    >
                      {installations.map((i) => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => navigate("/notifications")}
                title="Notificaciones"
                style={{ position: "relative" }}
                data-testid="bell-icon"
              >
                <Bell size={16} />
                {unread > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    background: "var(--danger)", color: "white",
                    borderRadius: 10, padding: "1px 6px",
                    fontSize: 10, fontWeight: 700, minWidth: 18, textAlign: "center",
                  }} data-testid="unread-count">{unread > 99 ? "99+" : unread}</span>
                )}
              </button>
              <div className="user-chip" data-testid="user-chip" style={{ cursor: "pointer" }} onClick={openProfile} title="Editar mi perfil de instalación">
                <div className="user-avatar">{(user?.name || user?.username || "U").charAt(0).toUpperCase()}</div>
                <div className="user-info">
                  <div className="user-name">{user?.name || user?.username}</div>
                  <div className="user-role">{user?.role}</div>
                </div>
              </div>
              <button className="btn btn-icon btn-ghost" onClick={logout} title="Cerrar sesión" data-testid="logout-btn">
                <LogOut size={16} />
              </button>
            </div>
          </div>
          <div className="content" style={{ minHeight: "calc(100vh - 70px)" }}>
            <Outlet />
          </div>
        </div>
      </div>

      <Modal open={showProfile} onClose={() => setShowProfile(false)} title="Mi Perfil — Instalación" size="md" testId="profile-modal">
        <div className="grid gap-3">
          <div>
            <label className="label">Nombre de la Instalación</label>
            <input
              type="text"
              className="input"
              value={profileForm.installation_name}
              onChange={(e) => setProfileForm({ ...profileForm, installation_name: e.target.value })}
              placeholder="Ej: Refinería Cadereyta"
              data-testid="profile-installation-name"
            />
            <div className="text-xs text-dim mt-1">Aparecerá en el sidebar, encabezado y en todos los reportes generados.</div>
          </div>
          <div>
            <label className="label">Ubicación (opcional)</label>
            <input
              type="text"
              className="input"
              value={profileForm.installation_location}
              onChange={(e) => setProfileForm({ ...profileForm, installation_location: e.target.value })}
              placeholder="Ej: Nuevo León, México"
              data-testid="profile-installation-location"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Área</label>
              <input
                type="text"
                className="input"
                value={profileForm.area}
                onChange={(e) => setProfileForm({ ...profileForm, area: e.target.value })}
                data-testid="profile-area"
              />
            </div>
            <div>
              <label className="label">Departamento</label>
              <input
                type="text"
                className="input"
                value={profileForm.department}
                onChange={(e) => setProfileForm({ ...profileForm, department: e.target.value })}
                data-testid="profile-department"
              />
            </div>
          </div>
          {profileError && (
            <div className="card" style={{ background: "rgba(229,57,53,0.10)", border: "1px solid var(--danger)" }} data-testid="profile-error">
              {profileError}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-1">
            <button className="btn" onClick={() => setShowProfile(false)} disabled={profileSaving} data-testid="profile-cancel">Cancelar</button>
            <button className="btn btn-primary" onClick={saveProfile} disabled={profileSaving} data-testid="profile-save">
              {profileSaving ? <span className="spinner" /> : <><Save size={14} /> Guardar</>}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
